<template>
  <component
    :is="tag"
    :class="[`accent-${accent}`, `color-${color}`]"
    data-component-name="Cards.DownloadLogo"
  >
    <SvgIcon name="logo" />

    <div class="links">
      <a :href="pngLink" download>
        <Buttons.Base fill="none" accent="black">
          PNG <SvgIcon name="download" />
        </Buttons.Base>
      </a>

      <a :href="svgLink" download>
        <Buttons.Base fill="none" accent="black">
          SVG <SvgIcon name="download" />
        </Buttons.Base>
      </a>
    </div>
  </component>
</template>

<script setup lang="ts">
import Buttons from '~/components/redesign/Buttons';

export type CardsDownloadLogoProps = {
  tag?: keyof HTMLElementTagNameMap;
  accent?: 'light' | 'dark';
  color?: 'purple' | 'orange' | 'white' | 'black';
};

const props = withDefaults(defineProps<CardsDownloadLogoProps>(), {
  tag: 'div',
  accent: 'light',
  color: 'black',
});

const pngLink = computed(
  () => `/redesign/images/media-kit-logos/logo-${props.color}.png`,
);
const svgLink = computed(
  () => `/redesign/images/media-kit-logos/logo-${props.color}.svg`,
);
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '_/mixins/flex';
@use '_/mixins/typo';
@use '_/mixins/media';
@use '_/mixins/component';
@use '_/variables/shadow';
@use '_/variables/color-roles';
@use '_/fn';

[data-component-name='Cards.DownloadLogo'] {
  @include flex.center-center;

  flex-direction: column;
  gap: 1rem 0;
  padding: 2rem;
  box-shadow: shadow.$elevation-1;

  @include media.tablet {
    padding: 1.5rem;
  }

  .links {
    @include flex.center-center;

    gap: 0 1rem;

    a {
      [data-component-name='Buttons.Base'] {
        padding: 0.75rem 1rem;

        @include typo.button-medium;

        svg {
          fill: fn.token('icon-primary');
        }
      }
    }
  }

  &.accent {
    &-light {
      background-color: fn.token('surf-cont-primary');
    }

    &-dark {
      background-color: fn.token('surf-cont-dark');

      .links {
        a {
          [data-component-name='Buttons.Base'] {
            color: fn.token('text-reversed');

            svg {
              fill: fn.token('icon-reversed');
            }
          }
        }
      }
    }
  }

  &.color {
    &-purple {
      svg {
        fill: map.get(color-roles.$accent, 'Secondary', 'Purple-50');
      }
    }

    &-orange {
      svg {
        fill: map.get(color-roles.$accent, 'Primary', 'Orange-50');
      }
    }

    &-white {
      svg {
        fill: fn.token('icon-reversed');
      }
    }

    &-black {
      svg {
        fill: map.get(color-roles.$neutral, 'Gray-10');
      }
    }
  }
}
</style>
